/* eslint-disable no-await-in-loop, no-param-reassign */

export function parseRetryAfterSeconds(headerValue, fallbackSeconds = 30) {
  if (!headerValue) return fallbackSeconds;

  const trimmed = String(headerValue).trim();
  if (/^\d+$/.test(trimmed)) {
    const seconds = parseInt(trimmed);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : fallbackSeconds;
  }

  const httpDate = Date.parse(trimmed);
  if (Number.isFinite(httpDate)) {
    const seconds = Math.ceil((httpDate - Date.now()) / 1000);
    return seconds > 0 ? seconds : fallbackSeconds;
  }

  return fallbackSeconds;
}

function notifyUpdate(state, handlers) {
  if (typeof handlers.onDelta === 'function') {
    handlers.onDelta(state.text, state.sources, state.files);
  }
}

function appendDelta(state, handlers, delta) {
  if (typeof delta !== 'string' || delta === '') return;
  state.text += delta;
  notifyUpdate(state, handlers);
}

function handleSourceUrl(state, handlers, event) {
  if (!event.sourceId || typeof event.url !== 'string') return;
  state.sources.set(String(event.sourceId), {
    url: event.url,
    title: typeof event.title === 'string' ? event.title : '',
  });
  notifyUpdate(state, handlers);
}

function handleSourceDocument(state, handlers, event) {
  if (!event.sourceId) return;
  state.sources.set(String(event.sourceId), {
    title:
      typeof event.title === 'string'
        ? event.title
        : event.filename || 'Dokument',
    filename: typeof event.filename === 'string' ? event.filename : '',
  });
  notifyUpdate(state, handlers);
}

function addFile(state, handlers, file) {
  if (!file || typeof file.url !== 'string' || file.url === '') return;

  const normalized = {
    url: file.url,
    mediaType: typeof file.mediaType === 'string' ? file.mediaType : '',
    filename: typeof file.filename === 'string' ? file.filename : '',
    insertAt: state.text.length,
  };
  state.files.push(normalized);

  if (typeof handlers.onFile === 'function') {
    handlers.onFile(normalized);
  }
  notifyUpdate(state, handlers);
}

function handleToolOutput(state, handlers, event) {
  if (!event.output || typeof event.output !== 'object') return;

  if (Array.isArray(event.output.images)) {
    event.output.images.forEach((image) => {
      let { url } = image;
      if ((!url || url === 'N/A') && image.base64) {
        url = `data:image/png;base64,${image.base64}`;
      }
      addFile(state, handlers, {
        url,
        mediaType: 'image/png',
        filename: image.name || 'generated-image.png',
      });
    });
  }

  if (Array.isArray(event.output.files)) {
    event.output.files.forEach((file) => {
      const mimeType =
        file.mimeType || file.mediaType || 'application/octet-stream';
      let { url } = file;
      if ((!url || url === 'N/A') && file.base64) {
        url = `data:${mimeType};base64,${file.base64}`;
      }
      addFile(state, handlers, {
        url,
        mediaType: mimeType,
        filename: file.name || file.filename || '',
      });
    });
  }
}

function handleError(state, handlers, event) {
  let message = 'Communication error.';
  if (typeof event.error === 'string') {
    message = event.error;
  } else if (typeof event.message === 'string') {
    message = event.message;
  }

  state.streamError = {
    message,
    status: Number.isFinite(event.status) ? event.status : 502,
    retryAfter: Number.isFinite(event.retryAfter) ? event.retryAfter : 0,
  };

  if (typeof handlers.onError === 'function') {
    handlers.onError(state.streamError);
  }
}

function extractMessageText(message) {
  if (!message || typeof message !== 'object') return '';
  if (typeof message.content === 'string') return message.content;
  if (Array.isArray(message.parts)) {
    return message.parts
      .map((part) =>
        part && part.type === 'text' && typeof part.text === 'string'
          ? part.text
          : '',
      )
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

function processJsonEvent(state, handlers, event) {
  if (!event || typeof event !== 'object') return;

  const type = typeof event.type === 'string' ? event.type : '';
  switch (type) {
    case 'text-delta':
      appendDelta(
        state,
        handlers,
        event.delta || event.textDelta || event.text,
      );
      break;

    case 'text':
      appendDelta(state, handlers, event.text);
      break;

    case 'source-url':
      handleSourceUrl(state, handlers, event);
      break;

    case 'source-document':
      handleSourceDocument(state, handlers, event);
      break;

    case 'file':
      addFile(state, handlers, event);
      break;

    case 'tool-output-available':
      handleToolOutput(state, handlers, event);
      break;

    case 'error':
      handleError(state, handlers, event);
      break;

    default:
      if (event.choices?.[0]?.delta?.content) {
        appendDelta(state, handlers, event.choices[0].delta.content);
      }
      if (Array.isArray(event.messages)) {
        const text = event.messages
          .map((message) => extractMessageText(message))
          .filter(Boolean)
          .join('\n');
        appendDelta(state, handlers, text);
      }
      break;
  }
}

function processJsonPayload(state, handlers, payload) {
  try {
    processJsonEvent(state, handlers, JSON.parse(payload));
  } catch {
    // Ignore malformed stream events. Network/read errors still surface.
  }
}

function processLine(state, handlers, line) {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith(':') || trimmed.startsWith('event:')) {
    return;
  }

  if (/^\d+:/.test(trimmed)) {
    const prefix = trimmed.indexOf(':');
    const code = trimmed.slice(0, prefix);
    const payload = trimmed.slice(prefix + 1);
    if (code === '0') {
      try {
        appendDelta(state, handlers, JSON.parse(payload));
      } catch {
        appendDelta(state, handlers, payload);
      }
    }
    return;
  }

  if (trimmed.startsWith('data:')) {
    const payload = trimmed.slice(5).trimStart();
    if (!payload || payload === '[DONE]') return;
    processJsonPayload(state, handlers, payload);
    return;
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    processJsonPayload(state, handlers, trimmed);
  }
}

function processBuffer(state, handlers) {
  let newlineIndex = state.buffer.indexOf('\n');
  while (newlineIndex !== -1) {
    const line = state.buffer.slice(0, newlineIndex).replace(/\r$/, '');
    state.buffer = state.buffer.slice(newlineIndex + 1);
    processLine(state, handlers, line);
    newlineIndex = state.buffer.indexOf('\n');
  }
}

function processFallbackBody(state, handlers) {
  if (
    state.streamError ||
    state.text ||
    state.files.length > 0 ||
    !state.rawBuffer.trim()
  ) {
    return;
  }

  try {
    const parsed = JSON.parse(state.rawBuffer.trim());
    if (Array.isArray(parsed.messages)) {
      appendDelta(
        state,
        handlers,
        parsed.messages
          .map((message) => extractMessageText(message))
          .filter(Boolean)
          .join('\n'),
      );
      return;
    }
    if (parsed.choices?.[0]?.message?.content) {
      appendDelta(state, handlers, parsed.choices[0].message.content);
      return;
    }
  } catch {
    // Last resort below.
  }

  appendDelta(state, handlers, state.rawBuffer);
}

export async function readBrandyStream(response, handlers = {}) {
  if (!response.body) {
    throw new Error('Streaming response body is not available.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const state = {
    text: '',
    rawBuffer: '',
    buffer: '',
    sources: new Map(),
    files: [],
    streamError: null,
  };

  try {
    let isReading = true;
    while (isReading) {
      const { done, value } = await reader.read();
      if (done) {
        isReading = false;
      } else {
        const chunk = decoder.decode(value, { stream: true });
        state.rawBuffer += chunk;
        state.buffer += chunk;
        processBuffer(state, handlers);
      }
    }

    if (state.buffer.trim()) {
      processLine(state, handlers, state.buffer);
    }

    processFallbackBody(state, handlers);
  } finally {
    reader.releaseLock();
  }

  return {
    text: state.text,
    sources: state.sources,
    files: state.files,
    streamError: state.streamError,
  };
}
